import type { TFunction } from 'i18next';

import { GridRecord } from '../../../common/components/Grid/GridDataTypes';
import numberFormatter from '../../../common/numberFormatter';
import { GetEstimateByMonthWithItemsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import mathUtils from '../../../utils/mathUtils';
import momencik from '../../../common/momencik';
import { ProjectEstimatesStatusObj } from '../../../common/pokCore/validation/enums';
import { TKeys } from '../../../translations';

export const getItemsGridData = (
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  items: GetEstimateByMonthWithItemsDto[],
) => {
  const data: GridRecord[] = [];

  for (const d of items) {
    let salesNet = 0;
    let salesCommissionAmount = 0;
    let salesNetTotal = 0;
    let salesInvoicesSum = 0;
    let purchaseNetTotal = 0;
    let purchaseInvoicesSum = 0;
    let purchaseAmountToAccept = 0;

    d.estimateItems?.map(rec => {
      salesNet = mathUtils.add(salesNet, mathUtils.round(rec.salesNet));
      salesCommissionAmount = mathUtils.add(
        salesCommissionAmount,
        mathUtils.round(rec.salesCommissionAmount),
      );
      salesNetTotal = mathUtils.add(
        salesNetTotal,
        mathUtils.round(rec.salesNetTotal),
      );
      salesInvoicesSum = mathUtils.add(
        salesInvoicesSum,
        mathUtils.round(rec.salesInvoicesSum),
      );
      purchaseNetTotal = mathUtils.add(
        purchaseNetTotal,
        mathUtils.round(rec.purchaseNetTotal),
      );
      purchaseInvoicesSum = mathUtils.add(
        purchaseInvoicesSum,
        mathUtils.round(rec.purchaseInvoicesSum),
      );
      purchaseAmountToAccept = mathUtils.add(
        purchaseAmountToAccept,
        mathUtils.round(rec.salesNet),
      );
    });

    data.push({
      key: d.id,
      values: {
        profile: d.profile?.name,
        month: momencik(d.date, 'YYYY-MM'),
        salesNet: numberFormatter(salesNet, true),
        salesCommissionAmount: numberFormatter(salesCommissionAmount, true),
        salesNetTotal: numberFormatter(salesNetTotal, true),
        salesInvoicesSum: numberFormatter(salesInvoicesSum, true),
        purchaseNetTotal: numberFormatter(purchaseNetTotal, true),
        purchaseInvoicesSum: numberFormatter(purchaseInvoicesSum, true),
        purchaseAmountToAccept: numberFormatter(
          mathUtils.subtract(purchaseNetTotal, purchaseInvoicesSum),
          true,
        ),
        margin: numberFormatter(
          mathUtils.subtract(salesNetTotal, purchaseNetTotal),
          true,
        ),
        status: t(ProjectEstimatesStatusObj[d.status]),
        salesLock: d.isSalesBlocked
          ? t(tk.common.yesUpperCase)
          : t(tk.common.noUpperCase),
        purchaseLock: d.isPurchaseBlocked
          ? t(tk.common.yesUpperCase)
          : t(tk.common.noUpperCase),
      },
      orderByNumberValues: {
        salesNet: salesNet,
        salesCommissionAmount: salesCommissionAmount,
        salesNetTotal: salesNetTotal,
        salesInvoicesSum: salesInvoicesSum,
        purchaseNetTotal: purchaseNetTotal,
        purchaseInvoicesSum: purchaseInvoicesSum,
        purchaseAmountToAccept: mathUtils.subtract(
          purchaseNetTotal,
          purchaseInvoicesSum,
        ),
      },
      tdCssClass: {
        salesNet: 'amounts-align-right',
        salesCommissionAmount: 'amounts-align-right',
        salesNetTotal: 'amounts-align-right',
        salesInvoicesSum: 'amounts-align-right',
        purchaseNetTotal: 'amounts-align-right',
        purchaseInvoicesSum: 'amounts-align-right',
        purchaseAmountToAccept: 'amounts-align-right',
        margin: 'amounts-align-right',
      },
    });
  }
  return data;
};

export const getItemsGridSummary = (
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  data: GetEstimateByMonthWithItemsDto[],
  keys: string[],
) => {
  let salesNet = 0;
  let salesCommissionAmount = 0;
  let salesNetTotal = 0;
  let salesInvoicesSum = 0;
  let purchaseNetTotal = 0;
  let purchaseInvoicesSum = 0;
  let purchaseAmountToAccept = 0;

  data = data.filter(item => keys.includes(item.id));

  data.map(month => {
    month.estimateItems?.map(item => {
      salesNet = mathUtils.add(salesNet, mathUtils.round(item.salesNet));
      salesCommissionAmount = mathUtils.add(
        salesCommissionAmount,
        mathUtils.round(item.salesCommissionAmount),
      );
      salesNetTotal = mathUtils.add(
        salesNetTotal,
        mathUtils.round(item.salesNetTotal),
      );
      salesInvoicesSum = mathUtils.add(
        salesInvoicesSum,
        mathUtils.round(item.salesInvoicesSum),
      );
      purchaseNetTotal = mathUtils.add(
        purchaseNetTotal,
        mathUtils.round(item.purchaseNetTotal),
      );
      purchaseInvoicesSum = mathUtils.add(
        purchaseInvoicesSum,
        mathUtils.round(item.purchaseInvoicesSum),
      );
      purchaseAmountToAccept = mathUtils.add(
        purchaseAmountToAccept,
        mathUtils.round(item.salesNet),
      );
    });
  });

  return {
    key: 'summary',
    values: {
      profile: t(tk.common.sumUpperCase),
      month: '',
      salesNet: numberFormatter(salesNet, true),
      salesCommissionAmount: numberFormatter(salesCommissionAmount, true),
      salesNetTotal: numberFormatter(salesNetTotal, true),
      salesInvoicesSum: numberFormatter(salesInvoicesSum, true),
      purchaseNetTotal: numberFormatter(purchaseNetTotal, true),
      purchaseInvoicesSum: numberFormatter(purchaseInvoicesSum, true),
      purchaseAmountToAccept: numberFormatter(
        mathUtils.subtract(purchaseNetTotal, purchaseInvoicesSum),
        true,
      ),
      margin: numberFormatter(
        mathUtils.subtract(salesNetTotal, purchaseNetTotal),
        true,
      ),
    },
    tdCssClass: {
      salesNet: 'amounts-align-right',
      salesCommissionAmount: 'amounts-align-right',
      salesNetTotal: 'amounts-align-right',
      salesInvoicesSum: 'amounts-align-right',
      purchaseNetTotal: 'amounts-align-right',
      purchaseInvoicesSum: 'amounts-align-right',
      purchaseAmountToAccept: 'amounts-align-right',
      margin: 'amounts-align-right',
    },
  };
};
